/**
 * Element tab of the editor (Paper "Experiments · 02 Editor"). Built for the
 * person changing copy, not CSS: version B's text sits over the Control text,
 * and a change applies on Enter or when the field loses focus (the shell
 * autosaves). Style keeps a short list of plain controls with raw CSS behind a
 * disclosure; Visibility owns show/hide, order, duplicate and remove.
 */
import { useEffect, useMemo, useRef, useState, type ReactNode } from "react"
import type { ParentBridge } from "@humblytics/editor-core"
import type { EditorElementData } from "@/lib/contexts/AISidebarContext"
import { cn } from "@/lib/utils"

type Tab = "content" | "style" | "visibility"

type EditorElementPanelProps = {
  element: EditorElementData
  bridge: ParentBridge
  /** Version being edited, e.g. "B" */
  versionId: string
  /** The element's text before any change in this draft; null when unknown (already changed on load) */
  controlText: string | null
  /** True when this element already has a saved or pending change on this version */
  hasChange: boolean
  /** The saved text change for this element on this version; the page can re-render over it */
  savedText?: string | null
  /** Drop every change on this element (the shell rewrites the saved list and reloads the page) */
  onResetToControl: () => void
  onDeselect: () => void
  /** Unique visitors a day on this page, last 30 days */
  pageDaily: number | null
}

const KIND_BY_TAG: Record<string, string> = {
  h1: "Heading", h2: "Heading", h3: "Heading", h4: "Heading", h5: "Heading", h6: "Heading",
  a: "Link", button: "Button", img: "Image", picture: "Image", svg: "Icon", video: "Video",
  p: "Paragraph", span: "Text", li: "List item", label: "Label", input: "Form field",
  textarea: "Form field", select: "Form field", form: "Form", nav: "Navigation",
  header: "Header", footer: "Footer", section: "Section", div: "Block", ul: "List", ol: "List",
}

const TEXTLESS = new Set(["img", "picture", "svg", "video", "input", "select", "form"])
/**
 * Layout containers: their text is every child's text run together, and
 * rewriting it would flatten the children into one string. Words are edited
 * on the text element itself.
 */
const CONTAINERS = new Set(["div", "section", "header", "footer", "nav", "main", "article", "aside", "ul", "ol", "form", "table", "tbody", "tr"])

function rgbToHex(value: string | undefined): string {
  if (!value) return ""
  if (value.startsWith("#")) return value
  const m = value.match(/^rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*([\d.]+))?/)
  if (!m) return ""
  // Translucent reads as none: the editor tints the selected element, so a
  // see-through background is the selection highlight, not the page's style.
  if (m[4] !== undefined && Number(m[4]) < 1) return ""
  return `#${[m[1], m[2], m[3]].map((n) => Number(n).toString(16).padStart(2, "0")).join("")}`
}

/** "section.hero › div.flex › h1.title": tag plus first class of the last three steps. */
function shortPath(selector: string): string {
  return selector
    .split(" > ")
    .slice(-3)
    .map((step) => step.trim().match(/^[a-z0-9]+(?:\.[A-Za-z][\w-]*)?/i)?.[0] ?? step)
    .join(" › ")
}

/** The plain style controls, read from the element's computed style. */
function readStyles(cs: Record<string, string> = {}) {
  return {
    color: rgbToHex(cs.color),
    "background-color": rgbToHex(cs["background-color"]),
    "font-size": cs["font-size"] || "",
    "font-weight": cs["font-weight"] || "",
    "text-align": cs["text-align"] || "",
    "border-radius": cs["border-radius"] || "",
    "padding-top": cs["padding-top"] || "",
    "padding-left": cs["padding-left"] || "",
  }
}
type StyleKey = keyof ReturnType<typeof readStyles>

export function EditorElementPanel({
  element,
  bridge,
  versionId,
  controlText,
  hasChange,
  savedText = null,
  onResetToControl,
  onDeselect,
  pageDaily,
}: EditorElementPanelProps) {
  const tag = element.tagName.toLowerCase()
  const kind = KIND_BY_TAG[tag] ?? "Element"
  const isContainer = CONTAINERS.has(tag)
  const hasText = !TEXTLESS.has(tag) && !isContainer && element.textContent.trim().length > 0
  const isImage = tag === "img"
  const hasContent = hasText || isImage
  const [tab, setTab] = useState<Tab>(hasContent ? "content" : "style")
  const [imageSrc, setImageSrc] = useState(element.src ?? "")
  const [imageAlt, setImageAlt] = useState(element.alt ?? "")

  // ── Content ──
  const startText = savedText ?? element.textContent.trim()
  const [text, setText] = useState(startText)
  const appliedRef = useRef(startText)
  const [justApplied, setJustApplied] = useState(false)
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  // ── Style ──
  const initialStyles = useMemo(() => readStyles(element.computedStyles), [element.computedStyles])
  const [styles, setStyles] = useState(initialStyles)
  const [customCss, setCustomCss] = useState("")
  const [showCustom, setShowCustom] = useState(false)
  const [hidden, setHidden] = useState(false)

  useEffect(() => {
    const t = savedText ?? element.textContent.trim()
    setText(t)
    appliedRef.current = t
    setJustApplied(false)
    setStyles(readStyles(element.computedStyles))
    setCustomCss("")
    setShowCustom(false)
    setHidden(false)
    setImageSrc(element.src ?? "")
    setImageAlt(element.alt ?? "")
    const tg = element.tagName.toLowerCase()
    setTab(tg === "img" || (!TEXTLESS.has(tg) && !CONTAINERS.has(tg) && t.length > 0) ? "content" : "style")
  }, [element.selector, element.tagName, element.textContent, element.computedStyles, element.src, element.alt, savedText])

  useEffect(() => {
    if (tab === "content") textareaRef.current?.focus()
  }, [tab, element.selector])

  const applyText = () => {
    const next = text.trim()
    if (!next || next === appliedRef.current) return
    bridge.send({ type: "APPLY_TEXT_CHANGE", payload: { selector: element.selector, text: next } })
    appliedRef.current = next
    setJustApplied(true)
    window.setTimeout(() => setJustApplied(false), 1600)
  }

  const previewStyle = (key: StyleKey, value: string) => {
    setStyles((prev) => ({ ...prev, [key]: value }))
    bridge.send({ type: "APPLY_STYLE_PREVIEW", payload: { selector: element.selector, property: key, value } })
  }
  const changedStyles = useMemo(() => {
    const out: Record<string, string> = {}
    for (const key of Object.keys(styles) as StyleKey[]) {
      if (styles[key] && styles[key] !== initialStyles[key]) out[key] = styles[key]
    }
    for (const line of customCss.split(/[;\n]/)) {
      const [prop, ...rest] = line.split(":")
      const value = rest.join(":").trim()
      if (prop?.trim() && value) out[prop.trim()] = value
    }
    // Spacing edits mirror to the opposite side so one field moves both.
    if (out["padding-top"]) out["padding-bottom"] = out["padding-top"]
    if (out["padding-left"]) out["padding-right"] = out["padding-left"]
    return out
  }, [styles, initialStyles, customCss])
  const styleDirty = Object.keys(changedStyles).length > 0

  const applyStyles = () => {
    if (!styleDirty) return
    bridge.send({ type: "APPLY_STYLE_CHANGES", payload: { selector: element.selector, styles: changedStyles } })
    setStyles((prev) => ({ ...prev }))
    setCustomCss("")
    setJustApplied(true)
    window.setTimeout(() => setJustApplied(false), 1600)
  }
  const discardStyles = () => {
    for (const key of Object.keys(initialStyles) as StyleKey[]) {
      bridge.send({ type: "APPLY_STYLE_PREVIEW", payload: { selector: element.selector, property: key, value: initialStyles[key] } })
    }
    setStyles(initialStyles)
    setCustomCss("")
  }

  const setVisible = (visible: boolean) => {
    setHidden(!visible)
    bridge.send({ type: "APPLY_VISIBILITY_CHANGE", payload: { selector: element.selector, visible } })
  }

  return (
    <div className="flex min-h-0 grow flex-col">
      {/* Element header */}
      <div className="flex flex-col gap-1 border-b border-ui-rowline px-4 pb-3.5 pt-4">
        <div className="flex items-center justify-between gap-3">
          <span className="flex min-w-0 items-center gap-2">
            <span className="text-sm font-semibold leading-[18px] text-ui-title">{kind}</span>
            {hasChange && (
              <span className="rounded-[5px] bg-primary-tint px-1.5 text-xs leading-[18px] text-primary-strong ring-1 ring-primary-tint-ring">
                Edited in {versionId}
              </span>
            )}
          </span>
          <span className="flex items-center gap-0.5">
            <IconAction label="Show it on the page" onClick={() => bridge.send({ type: "SELECTION_SET", payload: { selector: element.selector } } as never)}>
              <circle cx="12" cy="12" r="3" fill="none" stroke="currentColor" strokeWidth="2" />
              <path d="M12 2v3M12 19v3M2 12h3M19 12h3" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            </IconAction>
            <IconAction label="Close" onClick={() => { bridge.send({ type: "CANCEL_EDIT" }); onDeselect() }}>
              <path d="M6 6l12 12M18 6 6 18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            </IconAction>
          </span>
        </div>
        <span className="truncate font-mono text-xs leading-4 text-foreground/60" title={element.selector}>
          {shortPath(element.selector) || tag}
        </span>
      </div>

      {/* Content · Style · Visibility */}
      <div className="mx-4 mt-3 flex gap-0.5 rounded-lg bg-muted p-0.5" role="tablist" aria-label="Edit">
        {(["content", "style", "visibility"] as const).map((t) => (
          <button
            key={t}
            type="button"
            role="tab"
            aria-selected={tab === t}
            disabled={t === "content" && !hasContent}
            onClick={() => setTab(t)}
            className={cn(
              "flex h-[26px] grow basis-0 items-center justify-center rounded-[6px] text-xs font-medium capitalize transition-colors disabled:opacity-40",
              tab === t ? "bg-background text-ui-title shadow-[0_0_0_1px_rgb(0_0_0/0.06),0_1px_2px_rgb(0_0_0/0.06)]" : "text-foreground/60 hover:text-foreground",
            )}
          >
            {t}
          </button>
        ))}
      </div>

      {isContainer && (
        <p className="mx-4 mt-2.5 text-xs leading-[17px] text-foreground/60">
          This is a {kind.toLowerCase()} that holds other elements. To change words, click the text itself.
        </p>
      )}

      <div className="min-h-0 grow overflow-y-auto">
        {tab === "content" && hasText && (
          <div className="flex flex-col gap-4 px-4 py-[18px]">
            <div className="flex flex-col gap-2">
              <div className="flex justify-between text-xs leading-4 text-foreground/60">
                <label htmlFor="element-text">Variant {versionId}</label>
                <span className={cn("tabular-nums", text.length > 500 && "text-warning")}>
                  {justApplied ? "Applied" : `${text.length} / 500`}
                </span>
              </div>
              <textarea
                id="element-text"
                ref={textareaRef}
                value={text}
                maxLength={2000}
                rows={Math.min(8, Math.max(2, Math.ceil(text.length / 38)))}
                onChange={(e) => setText(e.target.value)}
                onBlur={applyText}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault()
                    applyText()
                  }
                  if (e.key === "Escape") {
                    setText(appliedRef.current)
                    ;(e.target as HTMLTextAreaElement).blur()
                  }
                }}
                className="w-full resize-none rounded-lg bg-background px-3 py-2.5 text-sm leading-5 text-ui-title outline-none ring-[1.5px] ring-foreground/80 transition-shadow focus:ring-foreground"
              />
              <span className="text-xs leading-4 text-foreground/60">Enter applies · Shift+Enter adds a line · Esc undoes</span>
            </div>

            {controlText != null && controlText !== text.trim() && (
              <div className="flex flex-col gap-2">
                <span className="text-xs leading-4 text-foreground/60">Control</span>
                <p className="rounded-lg bg-muted/60 px-3 py-2.5 text-sm leading-5 text-foreground/60 line-through decoration-1 ring-1 ring-ui-hairline">
                  {controlText}
                </p>
              </div>
            )}

            {hasChange && (
              <button type="button" onClick={onResetToControl} className="w-fit text-xs font-medium leading-4 text-foreground/60 hover:text-foreground">
                Reset to control
              </button>
            )}
          </div>
        )}

        {tab === "content" && isImage && (
          <div className="flex flex-col gap-4 px-4 py-[18px]">
            {imageSrc && (
              <img src={imageSrc} alt="" className="max-h-40 w-full rounded-lg bg-muted object-contain ring-1 ring-ui-hairline" />
            )}
            <div className="flex flex-col gap-2">
              <label htmlFor="element-image" className="text-xs leading-4 text-foreground/60">Image on version {versionId} · paste a URL</label>
              <TextField value={imageSrc} onChange={setImageSrc} placeholder="https://…/hero.png" label="Image URL" id="element-image" />
            </div>
            <div className="flex flex-col gap-2">
              <label htmlFor="element-alt" className="text-xs leading-4 text-foreground/60">Description for screen readers</label>
              <TextField value={imageAlt} onChange={setImageAlt} placeholder="What the image shows" label="Alt text" id="element-alt" />
            </div>
            <PlainButton
              className="w-fit"
              onClick={() => {
                if (!/^https?:\/\//i.test(imageSrc.trim())) return
                bridge.send({ type: "APPLY_IMAGE_CHANGE", payload: { selector: element.selector, src: imageSrc.trim(), alt: imageAlt.trim() || undefined } })
                setJustApplied(true)
                window.setTimeout(() => setJustApplied(false), 1600)
              }}
            >
              {justApplied ? "Applied" : `Use this image on ${versionId}`}
            </PlainButton>
            {hasChange && (
              <button type="button" onClick={onResetToControl} className="w-fit text-xs font-medium leading-4 text-foreground/60 hover:text-foreground">
                Reset to control
              </button>
            )}
          </div>
        )}

        {tab === "style" && (
          <div className="flex flex-col gap-4 px-4 py-[18px]">
            <Row label="Text color">
              <ColorField value={styles.color} onChange={(v) => previewStyle("color", v)} label="Text color" />
            </Row>
            <Row label="Background">
              <ColorField value={styles["background-color"]} onChange={(v) => previewStyle("background-color", v)} label="Background" />
            </Row>
            <Row label="Size">
              <TextField value={styles["font-size"]} onChange={(v) => previewStyle("font-size", v)} placeholder="As is, e.g. 48px" label="Font size" />
            </Row>
            <Row label="Weight">
              <Segmented
                value={String(styles["font-weight"])}
                options={[["400", "Regular"], ["500", "Medium"], ["600", "Semi"], ["700", "Bold"]]}
                onChange={(v) => previewStyle("font-weight", v)}
                label="Font weight"
              />
            </Row>
            <Row label="Align">
              <Segmented
                value={styles["text-align"] === "start" ? "left" : styles["text-align"]}
                options={[["left", "Left"], ["center", "Center"], ["right", "Right"]]}
                onChange={(v) => previewStyle("text-align", v)}
                label="Text align"
              />
            </Row>
            <Row label="Corners">
              <TextField value={styles["border-radius"]} onChange={(v) => previewStyle("border-radius", v)} placeholder="As is" label="Corner radius" />
            </Row>
            <Row label="Spacing">
              <div className="flex gap-2">
                <TextField value={styles["padding-top"]} onChange={(v) => previewStyle("padding-top", v)} placeholder="Top, bottom" label="Vertical padding" />
                <TextField value={styles["padding-left"]} onChange={(v) => previewStyle("padding-left", v)} placeholder="Sides" label="Horizontal padding" />
              </div>
            </Row>

            <div className="flex flex-col gap-2 border-t border-ui-rowline pt-3">
              <button
                type="button"
                aria-expanded={showCustom}
                onClick={() => setShowCustom((v) => !v)}
                className="flex w-fit items-center gap-1 text-xs font-medium leading-4 text-foreground/60 hover:text-foreground"
              >
                <svg width="12" height="12" viewBox="0 0 24 24" aria-hidden className={cn("transition-transform", showCustom && "rotate-90")}>
                  <path d="m9 18 6-6-6-6" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />
                </svg>
                Custom CSS
              </button>
              {showCustom && (
                <textarea
                  value={customCss}
                  onChange={(e) => setCustomCss(e.target.value)}
                  placeholder={"letter-spacing: -0.02em;\nline-height: 1.1;"}
                  rows={3}
                  aria-label="Custom CSS"
                  className="w-full resize-none rounded-lg bg-background px-3 py-2 font-mono text-xs leading-5 text-ui-title outline-none ring-1 ring-input focus:ring-ring"
                />
              )}
            </div>
          </div>
        )}

        {tab === "visibility" && (
          <div className="flex flex-col gap-5 px-4 py-[18px]">
            <div className="flex flex-col gap-2">
              <span className="text-xs leading-4 text-foreground/60">On version {versionId}</span>
              <Segmented
                value={hidden ? "hide" : "show"}
                options={[["show", "Shown"], ["hide", "Hidden"]]}
                onChange={(v) => setVisible(v === "show")}
                label="Visibility"
              />
              <span className="text-xs leading-[17px] text-foreground/60">
                Hiding removes it for visitors who see {versionId}. Control keeps it.
              </span>
            </div>
            <div className="flex flex-col gap-2">
              <span className="text-xs leading-4 text-foreground/60">Order on the page</span>
              <div className="flex gap-2">
                <PlainButton onClick={() => bridge.send({ type: "APPLY_REORDER", payload: { selector: element.selector, direction: "up" } })}>Move up</PlainButton>
                <PlainButton onClick={() => bridge.send({ type: "APPLY_REORDER", payload: { selector: element.selector, direction: "down" } })}>Move down</PlainButton>
                <PlainButton onClick={() => (bridge as unknown as { send: (m: unknown) => void }).send({ type: "APPLY_DUPLICATE", payload: { selector: element.selector } })}>Duplicate</PlainButton>
              </div>
            </div>
            <div className="flex flex-col gap-2 border-t border-ui-rowline pt-4">
              <button
                type="button"
                onClick={() => {
                  ;(bridge as unknown as { send: (m: unknown) => void }).send({ type: "APPLY_DELETE", payload: { selector: element.selector } })
                  onDeselect()
                }}
                className="w-fit text-[13px] font-medium leading-4 text-destructive hover:underline"
              >
                Remove from version {versionId}
              </button>
              <span className="text-xs leading-[17px] text-foreground/60">Undo in the top bar, or revert it in Changes.</span>
            </div>
          </div>
        )}
      </div>

      {tab === "style" && styleDirty && (
        <div className="flex gap-2 border-t border-border px-4 pb-4 pt-3">
          <PlainButton onClick={discardStyles} className="w-24">Discard</PlainButton>
          <button
            type="button"
            onClick={applyStyles}
            className="h-[34px] grow basis-0 rounded-lg bg-foreground text-[13px] font-medium text-background transition-opacity hover:opacity-90"
          >
            Apply to version {versionId}
          </button>
        </div>
      )}

      {pageDaily != null && tab !== "style" && (
        <div className="flex items-center justify-between border-t border-ui-rowline px-4 py-3 text-xs leading-4">
          <span className="text-foreground/60">Visitors a day on this page, last 30 days</span>
          <span className="font-medium tabular-nums text-ui-title">{pageDaily.toLocaleString("en-US")}</span>
        </div>
      )}
    </div>
  )
}

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="grid grid-cols-[84px_1fr] items-center gap-3">
      <span className="text-xs leading-4 text-foreground/60">{label}</span>
      {children}
    </div>
  )
}

function TextField({ value, onChange, placeholder, label, id }: { value: string; onChange: (v: string) => void; placeholder: string; label: string; id?: string }) {
  return (
    <input
      id={id}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      aria-label={label}
      className="h-8 w-full min-w-0 rounded-lg bg-background px-2.5 font-mono text-xs text-ui-title outline-none ring-1 ring-input placeholder:text-foreground/40 focus:ring-ring"
    />
  )
}

function ColorField({ value, onChange, label }: { value: string; onChange: (v: string) => void; label: string }) {
  return (
    <div className="flex items-center gap-2">
      <label className="relative size-8 shrink-0 cursor-pointer overflow-hidden rounded-lg ring-1 ring-input">
        <span className="absolute inset-0" style={{ background: value || "transparent" }} />
        {!value && <span className="absolute inset-0 bg-[linear-gradient(135deg,transparent_45%,rgb(0_0_0/0.25)_45%,rgb(0_0_0/0.25)_55%,transparent_55%)]" />}
        <input type="color" value={value || "#000000"} onChange={(e) => onChange(e.target.value)} aria-label={`${label} picker`} className="absolute inset-0 cursor-pointer opacity-0" />
      </label>
      <TextField value={value} onChange={onChange} placeholder="None" label={label} />
    </div>
  )
}

function Segmented({ value, options, onChange, label }: { value: string; options: Array<[string, string]>; onChange: (v: string) => void; label: string }) {
  return (
    <div className="flex gap-0.5 rounded-lg bg-muted p-0.5" role="radiogroup" aria-label={label}>
      {options.map(([v, text]) => (
        <button
          key={v}
          type="button"
          role="radio"
          aria-checked={value === v}
          onClick={() => onChange(v)}
          className={cn(
            "h-[26px] grow basis-0 rounded-[6px] px-1.5 text-xs transition-colors",
            value === v ? "bg-background font-medium text-ui-title shadow-[0_0_0_1px_rgb(0_0_0/0.06),0_1px_2px_rgb(0_0_0/0.06)]" : "text-foreground/60 hover:text-foreground",
          )}
        >
          {text}
        </button>
      ))}
    </div>
  )
}

function PlainButton({ onClick, children, className }: { onClick: () => void; children: ReactNode; className?: string }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn("h-8 rounded-lg px-3 text-[13px] font-medium text-ui-ink ring-1 ring-black/[0.08] transition-colors hover:bg-muted dark:ring-white/10", className)}
    >
      {children}
    </button>
  )
}

function IconAction({ label, onClick, children }: { label: string; onClick: () => void; children: ReactNode }) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      onClick={onClick}
      className="flex size-7 items-center justify-center rounded-[6px] text-ui-icon transition-colors hover:bg-muted hover:text-ui-ink"
    >
      <svg width="14" height="14" viewBox="0 0 24 24" aria-hidden>{children}</svg>
    </button>
  )
}

export default EditorElementPanel
